import {
  ChangeEvent,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import * as R from 'ramda';
import * as Dfns from 'date-fns/fp';
import Papa from 'papaparse';
import cn from 'classnames';
import {
  MdCheck,
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdDeleteForever,
  MdEdit,
  MdError,
  MdOutlineFileDownload,
  MdSave
} from 'react-icons/md';
import { Col, Form, InputGroup, Modal, OverlayTrigger, Row, Toast, Tooltip } from 'react-bootstrap';
import { TYPES_TO_NAMES } from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { FilterColumn } from '@blisspointmedia/bpm-types/dist/FilterBar';

import { download } from '@src/utils/download-utils';
import { useSetAreYouSure, useSetError } from '@src/redux/modals';
import { useAppSelector } from '@src/redux';
import { isInternalSelector } from '@src/redux/user';
import { useExperimentFlag } from '@src/utils/experiment-context';
import { useLocationCompany } from '@src/utils/hooks/useLocationCompany';
import { useStateFunction } from '@src/utils/hooks/useData';
import { pollS3 } from '@src/utils/fetch-utils';

import { Checkbox } from '@Components/FormCheck';
import { Page } from '@Components/Page';
import {
  BPMTable,
  CellRenderer,
  Header,
  SortParam,
  SuperTopItemType
} from '@Components/StickyTable';
import { NumberFormatter } from '@Components/NumberFormatter/NumberFormatter';
import { DatePicker } from '@Components/DatePicker';
import { Skeleton, TableSkeleton } from '@Components/Skeleton/Skeleton';
import { FilterBar } from '@Components/FilterBar';
import { Button } from '@Components/Button';
import { IconButton } from '@Components/IconButton';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { Dropdown } from '@Components/Dropdown';
import { InteractiveDiv } from '@Components/InteractiveEl';
import {
  Appearance,
  Intent,
  Placement,
  PlacementType,
  Size,
  StretchMode
} from '@Components/designConstants';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';

import {
  BY_COUNT_LABEL,
  BY_SPEND_LABEL,
  CURRENT_WEEK,
  DATE_FORMAT,
  DAYS_BY_INDEX,
  DnrRow,
  EstimateInfo,
  EXPANDED_LABEL,
  NETWORK_AVAIL_LABEL,
  NOTE_PRESET_OPTIONS,
  PLAN_DETAIL_CSV_HEADERS,
  PLAN_DETAIL_KEYS_TO_SUM,
  PlanRow,
  PlansData,
  SpotRow
} from './linear-plans-constants';
import {
  Clearance,
  clearanceForRow,
  currencyProps,
  joinNotes,
  splitNotes
} from './linear-plans-utils';
import {
  deleteSpots,
  fetchNetworkSummary,
  saveReconciliation,
  saveSpots,
  useLinearPlans
} from './use-linear-plans';
import './LinearPlans.scss';

interface ClickToEditCellProps {
  onClick: () => void;
  children: ReactNode;
}

// A table cell whose contents open the row editor on click, with a "Click to edit" tooltip.
const ClickToEditCell = ({ onClick, children }: ClickToEditCellProps) => (
  <OverlayTrigger placement={Placement.BOTTOM} overlay={<Tooltip>Click to edit</Tooltip>}>
    <InteractiveDiv className='selectable-cell' onClick={onClick}>
      {children}
    </InteractiveDiv>
  </OverlayTrigger>
);

interface ConditionalTooltipProps {
  show: boolean;
  placement: PlacementType;
  tooltip: ReactNode;
  children: ReactElement;
}

// Wraps children in a tooltip overlay only when `show` is true; otherwise renders them bare.
const ConditionalTooltip = ({ show, placement, tooltip, children }: ConditionalTooltipProps) => {
  if (!show) {
    return children;
  }
  return (
    <OverlayTrigger placement={placement} overlay={<Tooltip>{tooltip}</Tooltip>}>
      {children}
    </OverlayTrigger>
  );
};

// The table body has no loading affordance of its own (BPMTable has no loading concept, and
// Page's isLoading is telemetry-only), so this fills BPMTable's noRowsRenderer: a skeleton while
// fetching, otherwise the truly-empty fallback. (Filtered-to-nothing is handled by BPMTable's
// own FilteredEmptyState, which preempts noRowsRenderer.)
const TableLoading = ({ fetching }: { fetching: boolean }) => {
  if (fetching) {
    return (
      <Skeleton>
        <TableSkeleton />
      </Skeleton>
    );
  }
  return <div>No rows to show.</div>;
};

interface ExportSpotSummaryConfirmationProps {
  company: string;
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
}

const ExportSpotSummaryConfirmation = ({
  company,
  show,
  setShow
}: ExportSpotSummaryConfirmationProps) => {
  const [dayType, setDayType] = useState('Calendar');
  const [start, setStart] = useState(CURRENT_WEEK);
  const [end, setEnd] = useState(
    R.pipe(Dfns.parseISO, Dfns.addDays(7), Dfns.format(DATE_FORMAT))(CURRENT_WEEK)
  );
  const [downloading, setDownloading] = useState(false);

  return (
    <Modal
      size='sm'
      show={show}
      onHide={() => setShow(false)}
      dialogClassName='export-summary-modal'
    >
      <Modal.Header closeButton>
        <Modal.Title>Export Network Summary</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='export-summary-field'>
          <Form.Label>Day Type</Form.Label>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            value={dayType}
            options={['Calendar', 'Broadcast']}
            onChange={setDayType}
            stretch={StretchMode.FIT}
          />
        </div>
        <div className='export-summary-field'>
          <Form.Label>Start (Inclusive) – End (Exclusive)</Form.Label>
          <DatePicker
            range={{ start, end }}
            isOutsideRange={() => false}
            onChange={({ start: newStart, end: newEnd }) => {
              if (newStart && newEnd) {
                setStart(newStart);
                setEnd(newEnd);
              }
            }}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          design={Intent.SUCCESS}
          leadingElement={<MdOutlineFileDownload />}
          loading={downloading}
          onClick={async () => {
            setDownloading(true);
            const csv = await fetchNetworkSummary({
              company,
              start,
              end,
              calendar: dayType === 'Calendar'
            });
            download(
              csv,
              `Network Summary ${company} ${dayType} ${start} - ${end}.csv`,
              'text/csv'
            );
            setDownloading(false);
            setShow(false);
          }}
        >
          Download
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface EditSpotsModalProps {
  selectedSpots: SpotRow[];
  updateSelectedSpots: (newValues: Partial<SpotRow>) => void;
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
}

const EditSpotsModal = ({
  selectedSpots,
  updateSelectedSpots,
  show,
  setShow
}: EditSpotsModalProps) => {
  const firstSpot = selectedSpots[0];
  const [accepted, setAccepted] = useState(firstSpot ? firstSpot.accepted : false);
  const [cost, setCost] = useState(firstSpot ? firstSpot.cost : 0);
  const [creditAmount, setCreditAmount] = useState(firstSpot ? firstSpot.credit_amount : 0);
  const [costString, setCostString] = useState(String(cost));
  const [creditAmountString, setCreditAmountString] = useState(String(creditAmount));
  const [reconciled, setReconciled] = useState<number | string | undefined>(
    firstSpot ? firstSpot.reconciled : -1
  );
  const [notes, setNotes] = useState(firstSpot ? (firstSpot.notes ?? '') : '');
  const enableCAD = useExperimentFlag('enableCADLinearCompanies');

  const creditMemoStatus = firstSpot?.creditMemoStatus ?? null;

  const isCreditValid = Number(cost) >= Number(creditAmount);

  if (selectedSpots.length === 0) {
    setShow(false);
    return null;
  }

  const updateTargetValue = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    realValueSetter: (value: number) => void,
    formValueSetter: (value: string) => void
  ) => {
    const target = event.target.value.replace(/[^\d.]/g, '');
    if (!Number.isNaN(parseFloat(target))) {
      // handle allowing decimal places but overwriting leading zeros
      if (/.*\..*/.test(target)) {
        formValueSetter(target);
      } else {
        formValueSetter(String(parseFloat(target)));
      }
      realValueSetter(parseFloat(target));
    } else {
      formValueSetter('0');
      realValueSetter(0);
    }
  };

  return (
    <Modal size='lg' show={show} onHide={() => setShow(false)} dialogClassName='linear-plans-modal'>
      <Modal.Header closeButton>
        <Modal.Title>Edit Rows</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Form.Group as={Col}>
            <Form.Label>Accepted</Form.Label>
            <Checkbox checked={accepted} onChange={e => setAccepted(e.target.checked)} />
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Reconciled</Form.Label>
            <Form.Control
              value={reconciled ?? ''}
              onChange={e => updateTargetValue(e, () => {}, setReconciled)}
            />
          </Form.Group>
        </Row>

        <ConditionalTooltip
          show={creditMemoStatus !== null}
          placement={Placement.BOTTOM}
          tooltip='Editing is disabled because a credit memo exists for this spot. Delete the credit memo to edit'
        >
          <Row>
            <Form.Group as={Col}>
              <Form.Label>Cost</Form.Label>
              <InputGroup>
                <InputGroup.Text>{enableCAD ? 'CA$' : '$'}</InputGroup.Text>
                <Form.Control
                  value={costString}
                  onChange={e => updateTargetValue(e, setCost, setCostString)}
                  disabled={creditMemoStatus !== null}
                />
              </InputGroup>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label>Credit Amount</Form.Label>
              <InputGroup>
                <InputGroup.Text>{enableCAD ? 'CA$' : '$'}</InputGroup.Text>
                <Form.Control
                  value={creditAmountString}
                  onChange={e => updateTargetValue(e, setCreditAmount, setCreditAmountString)}
                  disabled={creditMemoStatus !== null}
                />
              </InputGroup>
            </Form.Group>
          </Row>
        </ConditionalTooltip>

        <Row>
          <Form.Group as={Col}>
            <Form.Label>Notes</Form.Label>
            <Dropdown
              searchable
              creatable
              multiSelect
              showChips
              clearable
              ariaLabel='Notes'
              placeholder='Select...'
              options={NOTE_PRESET_OPTIONS}
              value={splitNotes(notes)}
              onChange={values => setNotes(joinNotes(values))}
            />
          </Form.Group>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <ConditionalTooltip
          show={!isCreditValid}
          placement={Placement.LEFT}
          tooltip='Credit cannot be greater than cost'
        >
          <div className='edit-spots-done'>
            {!isCreditValid && <div className='edit-spots-blocker' />}
            <Button
              design={Intent.SUCCESS}
              disabled={!isCreditValid}
              onClick={() => {
                updateSelectedSpots({
                  accepted,
                  cost,
                  credit_amount: creditAmount,
                  reconciled,
                  notes
                });
                setShow(false);
              }}
            >
              Done
            </Button>
          </div>
        </ConditionalTooltip>
      </Modal.Footer>
    </Modal>
  );
};

interface CreditMemoToastsProps {
  nCreditMemosToast: number;
  setNCreditMemosToast: Dispatch<SetStateAction<number>>;
  showMakingCreditMemoToast: boolean;
  setShowMakingCreditMemoToast: Dispatch<SetStateAction<boolean>>;
}

const CreditMemoToasts = ({
  nCreditMemosToast,
  setNCreditMemosToast,
  showMakingCreditMemoToast,
  setShowMakingCreditMemoToast
}: CreditMemoToastsProps) => (
  <>
    <Toast
      className='linear-plans-toast'
      onClose={() => setNCreditMemosToast(0)}
      show={nCreditMemosToast > 0}
      delay={5000}
      autohide
    >
      <Toast.Header>
        <strong>
          {nCreditMemosToast} Credit Memo{nCreditMemosToast === 1 ? '' : 's'} created
        </strong>
        <small className='toast-timestamp'>just now</small>
      </Toast.Header>
      <Toast.Body>
        Go to the Credit Memo page to view {nCreditMemosToast === 1 ? 'it' : 'them'}.
      </Toast.Body>
    </Toast>
    <Toast
      className='linear-plans-toast'
      onClose={() => setShowMakingCreditMemoToast(false)}
      show={showMakingCreditMemoToast}
      delay={5000}
      autohide
    >
      <Toast.Header>
        <strong>Creating one or more credit memos</strong>
      </Toast.Header>
      <Toast.Body>This might take a while.</Toast.Body>
    </Toast>
  </>
);

export const LinearPlans = () => {
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();
  const company = useLocationCompany() ?? '';
  const [startWeek, setStartWeek] = useState(CURRENT_WEEK);
  const [endWeek, setEndWeek] = useState(CURRENT_WEEK);
  const { data: fetchedData, isLoading, error } = useLinearPlans(company, startWeek, endWeek);
  // Local editable working copy of the fetched data: rows carry client-only state (selection,
  // dirty inline edits) that persists until saved, so we layer edits on top of the query result.
  const [data, setData] = useState<PlansData | undefined>();
  const [downloadingUrl, setDownloadingUrl] = useState<string | undefined>();
  const [displayByCount, setDisplayByCount] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showExportSpotSummaryConfirmation, setShowExportSpotSummaryConfirmation] = useState(false);
  const [showEditSelectedRows, setShowEditSelectedRows] = useState(false);
  const [saving, setSaving] = useState(false);
  const [planDetailFilter, setPlanDetailFilter] = useStateFunction<(line: PlanRow) => boolean>(
    () => true
  );
  const [spotDetailFilter, setSpotDetailFilter] = useStateFunction<(line: SpotRow) => boolean>(
    () => true
  );
  const [planFilterAdvanced, setPlanFilterAdvanced] = useState(false);
  const [spotFilterAdvanced, setSpotFilterAdvanced] = useState(false);
  const [spotDetailSortParamList, setSpotDetailSortParamList] = useState<SortParam[]>([]);
  const [nCreditMemosToast, setNCreditMemosToast] = useState(0);
  const [showMakingCreditMemoToast, setShowMakingCreditMemoToast] = useState(false);
  const isInternal = useAppSelector(isInternalSelector);
  const enableCAD = useExperimentFlag('enableCADLinearCompanies');

  const updateReconcileStatus = useCallback(
    async (row: DnrRow & { status: string }) => {
      if (!data) {
        return;
      }
      let { dnr } = data;
      if (row.status === '') {
        dnr = dnr.filter(
          missingRow =>
            row.week !== missingRow.week ||
            row.company !== missingRow.company ||
            row.network !== missingRow.network ||
            row.avail !== missingRow.avail
        );
      } else if (row.status === 'DNR') {
        dnr = [...dnr, row];
      }

      setData({ ...data, dnr });
      await saveReconciliation(row);
    },
    [data]
  );

  const fileDownloadColumn = useCallback(
    (label: string, name: string): Header => ({
      label,
      name,
      width: 60,
      renderer: row => {
        if (!row[name]) {
          return null;
        }
        return (
          <IconButton
            appearance={Appearance.EMPTY}
            loading={downloadingUrl === row[name]}
            onClick={async () => {
              if (row[name]) {
                const parts = row[name].match(/([^/]+)\/(.*)/);
                if (parts && parts.length >= 2) {
                  setDownloadingUrl(row[name]);
                  await pollS3({
                    bucket: parts[1],
                    filename: parts[2],
                    mimeType: 'application/octet-stream'
                  });
                  setDownloadingUrl(undefined);
                } else {
                  setError({ message: `Invalid parts ${row[name]}` });
                }
              }
            }}
          >
            <MdOutlineFileDownload />
          </IconButton>
        );
      }
    }),
    [downloadingUrl, setError]
  );

  const isEditable = useMemo(() => {
    if (!data) {
      return false;
    }

    const costs = R.uniq(data.spots.filter(r => r.selected).map(r => r.cost));
    return costs.length === 1;
  }, [data]);

  const updateSpots = useCallback(async () => {
    if (!data) {
      return;
    }
    setSaving(true);
    try {
      // notify user if credit memos are being made because the
      // query will take longer in that case
      if (
        data.spots.filter(row => row.credit_amount > 0 && row.creditMemoStatus === null).length > 0
      ) {
        setShowMakingCreditMemoToast(true);
      }
      const { spotsWithCreditMemos, nMemosCreated } = await saveSpots(
        data.spots.filter(row => row.dirty)
      );
      setNCreditMemosToast(nMemosCreated);

      setData({
        ...data,
        spots: data.spots.map(spotRow => ({
          ...spotRow,
          dirty: false,
          creditMemoStatus: spotsWithCreditMemos.includes(spotRow.id)
            ? 'Pending'
            : spotRow.creditMemoStatus
        }))
      });
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
    setSaving(false);
  }, [data, setError]);

  const handleDeleteSpots = useCallback(async () => {
    if (!data) {
      return;
    }
    setSaving(true);
    await deleteSpots(data.spots.filter(row => row.selected));
    setData({
      ...data,
      spots: data.spots.filter(spotRow => !spotRow.selected)
    });
    setSaving(false);
  }, [data]);

  const updateSpot = useCallback(
    (row: SpotRow) => {
      if (!data) {
        return;
      }
      setData({
        ...data,
        spots: data.spots.map(spotRow => (spotRow.id === row.id ? row : spotRow))
      });
    },
    [data]
  );

  const editSingleRow = useCallback(
    (row: SpotRow) => {
      if (!data) {
        return;
      }
      setData({
        ...data,
        spots: data.spots.map(spotRow => ({ ...spotRow, selected: spotRow.id === row.id }))
      });
      setShowEditSelectedRows(true);
    },
    [data]
  );

  const spotDetailHeaders = useMemo<Header[]>(
    () => [
      ...(isInternal
        ? [
            {
              label: '',
              name: 'selected',
              width: 40,
              nonInteractive: true,
              renderer: row => (
                <IconButton
                  appearance={Appearance.EMPTY}
                  onClick={() => {
                    updateSpot({ ...row, selected: !row.selected });
                  }}
                >
                  {row.selected ? <MdCheckBox /> : <MdCheckBoxOutlineBlank />}
                </IconButton>
              )
            }
          ]
        : []),
      ...(startWeek !== endWeek ? [{ label: 'Week', name: 'week' }] : []),
      {
        label: 'Network',
        name: 'network',
        width: 160,
        renderer: row =>
          row.network.indexOf('DSH') === 0 ||
          row.network.indexOf('DT') === 0 ||
          row.network.indexOf('ITN') === 0 ||
          row.network.indexOf('CMAC') === 0 ||
          row.network.indexOf('CMFC') === 0 ||
          row.network.indexOf('CMMC') === 0 ||
          row.network.indexOf('CMSC') === 0
            ? `${row.network} / ${row.satellite_network}`
            : row.network
      },
      { label: 'Avail', name: 'avail', width: 60 },
      { label: 'ISCI', name: 'isci', width: 120 },
      { label: 'Len', name: 'length', width: 60 },
      { label: 'Type', name: 'type', width: 70, renderer: row => TYPES_TO_NAMES[`${row.type}`] },
      {
        label: 'Broadcast Time',
        name: 'datetime_broadcast',
        width: 220,
        renderer: row => `${row.datetime_broadcast_text}${row.adjusted_timestamp ? ' *' : ''}`
      },
      {
        label: 'Calendar Time (Eastern)',
        name: 'datetime_calendar',
        width: 220,
        renderer: row => `${row.datetime_calendar_text}${row.adjusted_timestamp ? ' *' : ''}`
      },
      ...(expanded ? [{ label: 'Selling Rotation', name: 'rotation', width: 300 }] : []),
      ...(expanded
        ? [
            {
              label: 'Media Classification',
              name: 'mediaClassification',
              flex: 1,
              minFlexWidth: 150
            }
          ]
        : []),
      ...(expanded ? [{ label: 'Campaign', name: 'campaign', flex: 1, minFlexWidth: 150 }] : []),
      { label: 'Program', name: 'program', flex: 1, minFlexWidth: 200 },
      {
        label: 'Cost',
        name: 'cost',
        width: 100,
        renderer: row => (
          <ClickToEditCell onClick={() => editSingleRow(row)}>
            <NumberFormatter {...currencyProps(enableCAD)} value={row.cost} type='$' />
          </ClickToEditCell>
        )
      },
      {
        label: 'Credit Amount',
        name: 'credit_amount',
        width: 100,
        renderer: row => (
          <ClickToEditCell onClick={() => editSingleRow(row)}>
            <NumberFormatter {...currencyProps(enableCAD)} value={row.credit_amount} type='$' />
          </ClickToEditCell>
        )
      },
      {
        label: 'Accept',
        name: 'accepted',
        width: 70,
        renderer: row => (
          <ClickToEditCell onClick={() => editSingleRow(row)}>
            {row.accepted ? <MdCheckBox /> : <MdCheckBoxOutlineBlank />}
          </ClickToEditCell>
        )
      },
      {
        label: 'Notes',
        name: 'notes',
        flex: 1,
        minFlexWidth: 200,
        renderer: row => (
          <ClickToEditCell onClick={() => editSingleRow(row)}>{row.notes}</ClickToEditCell>
        )
      },
      fileDownloadColumn('Logs', 'log_file'),
      fileDownloadColumn('Order', 'order_file'),
      fileDownloadColumn('Traffic', 'traffic_file'),
      { label: 'BVS', name: 'bvs', width: 40, renderer: row => (row.bvs ? <MdCheck /> : null) },
      {
        label: 'Pre',
        name: 'prelog',
        width: 40,
        renderer: row => (row.prelog ? <MdCheck /> : null)
      },
      {
        label: 'Credit Memo Status',
        name: 'creditMemoStatus',
        width: 180,
        renderer: row =>
          row.creditMemoStatus ? (
            <span className='credit-memo-status'>{row.creditMemoStatus}</span>
          ) : (
            'Not Requested'
          )
      },
      ...(expanded
        ? [
            {
              label: 'Reconciled',
              name: 'reconciled',
              renderer: row => (
                <ClickToEditCell onClick={() => editSingleRow(row)}>
                  {row.reconciled}
                </ClickToEditCell>
              )
            }
          ]
        : []),
      ...(expanded ? [{ label: 'Last Modified', name: 'lastmodified_text', width: 220 }] : []),
      ...(expanded ? [{ label: 'Last Dropped', name: 'lastdropped_text', width: 220 }] : [])
    ],
    [
      startWeek,
      endWeek,
      fileDownloadColumn,
      isInternal,
      updateSpot,
      expanded,
      enableCAD,
      editSingleRow
    ]
  );

  const planDetailCsv = useMemo(() => {
    const header = PLAN_DETAIL_CSV_HEADERS.map(h => h.label);
    const rows: unknown[][] = data
      ? data.plans.map(row => {
          const csvRow: Record<string, unknown> = {
            ...row,
            booked_m: row.booked[0].count,
            booked_tu: row.booked[1].count,
            booked_w: row.booked[2].count,
            booked_th: row.booked[3].count,
            booked_f: row.booked[4].count,
            booked_sa: row.booked[5].count,
            booked_su: row.booked[6].count,
            cleared_m: row.cleared[0].count,
            cleared_tu: row.cleared[1].count,
            cleared_w: row.cleared[2].count,
            cleared_th: row.cleared[3].count,
            cleared_f: row.cleared[4].count,
            cleared_sa: row.cleared[5].count,
            cleared_su: row.cleared[6].count,
            cleared_rate: row.estimateInfo.clearanceRate,
            cleared_hist: row.estimateInfo.historicalClearanceRate
          };
          return PLAN_DETAIL_CSV_HEADERS.map(h => csvRow[h.name]);
        })
      : [];

    return Papa.unparse([header, ...rows]);
  }, [data]);

  const actions = useMemo(
    () => (
      <div className='linear-plans-actions'>
        <div className='linear-plans-legend'>
          <div className='at-goal'>At Goal</div>
          <div className='below-goal'>Under Goal</div>
          <div className='above-goal'>Above Goal</div>
        </div>

        <SegmentedControlButton
          size={Size.LG}
          options={[BY_COUNT_LABEL, BY_SPEND_LABEL]}
          selectedOption={displayByCount ? BY_COUNT_LABEL : BY_SPEND_LABEL}
          onChange={val => setDisplayByCount(val === BY_COUNT_LABEL)}
        />
        <SegmentedControlButton
          size={Size.LG}
          options={[EXPANDED_LABEL, NETWORK_AVAIL_LABEL]}
          selectedOption={expanded ? EXPANDED_LABEL : NETWORK_AVAIL_LABEL}
          onChange={val => setExpanded(val === EXPANDED_LABEL)}
        />
        <DatePicker
          range={{ start: startWeek, end: endWeek }}
          fullWeeksOnly
          isOutsideRange={() => false}
          isDayBlocked={date => Dfns.getDay(Dfns.parseISO(date)) !== 1}
          onChange={({ start, end }) => {
            if (start && end) {
              setStartWeek(start);
              setEndWeek(end);
            }
          }}
        />
      </div>
    ),
    [displayByCount, expanded, startWeek, endWeek]
  );

  // Seed/refresh the local working copy whenever the query returns new server data.
  useEffect(() => {
    setData(fetchedData);
  }, [fetchedData]);

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load plans",
        message: `Failed to load data: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  const createDayHeader = useCallback(
    (field: 'booked' | 'cleared', index: number, highlight = false): Header => ({
      label: DAYS_BY_INDEX[index],
      name: `${field}_${DAYS_BY_INDEX[index].toLowerCase()}`,
      width: displayByCount ? 40 : 80,
      nonInteractive: true,
      renderer: row => {
        let goalStyle = 'at-goal';
        if (row.clearance === Clearance.ABOVE_GOAL) {
          goalStyle = 'above-goal';
        } else if (row.clearance === Clearance.BELOW_GOAL || row.clearance === Clearance.NONE) {
          goalStyle = 'below-goal';
        }

        if (!row[field] || !row[field][index]) {
          return null;
        }

        const showHighlight = highlight && index <= row?.estimateInfo?.prelogLastDate;

        return (
          <div className={cn({ 'highlight-cell': showHighlight, [goalStyle]: showHighlight })}>
            {displayByCount ? (
              <NumberFormatter value={row[field][index].count} type='#' />
            ) : (
              <NumberFormatter
                {...currencyProps(enableCAD)}
                value={row[field][index].spend}
                type='$'
              />
            )}
          </div>
        );
      }
    }),
    [displayByCount, enableCAD]
  );

  const planDetailHeaders = useMemo<Header[]>(
    () => [
      ...(startWeek !== endWeek
        ? [{ label: 'Week', name: 'week', sortPriority: 1, sortAscending: true }]
        : []),
      { label: 'Network', name: 'network', width: 160, sortPriority: 2, sortAscending: true },
      { label: 'Avail', name: 'avail', sortPriority: 3, sortAscending: true, width: 60 },
      ...(!expanded
        ? [
            {
              label: 'BVS',
              name: 'bvs',
              width: 60,
              renderer: row => (row.bvs ? <MdCheck /> : null)
            }
          ]
        : []),
      ...(!expanded
        ? [
            {
              label: 'Pre',
              name: 'prelog',
              width: 60,
              renderer: row => (row.prelog ? <MdCheck /> : null)
            }
          ]
        : []),
      ...(!expanded
        ? [
            {
              label: 'Post',
              name: 'postlog',
              width: 60,
              renderer: row => {
                if (row.postlog) {
                  return <MdCheck />;
                }
                if (row.dnr) {
                  return (
                    <Button
                      size={Size.LG}
                      appearance={Appearance.EMPTY}
                      onClick={async () => updateReconcileStatus({ ...row, status: '' })}
                    >
                      DNR
                    </Button>
                  );
                }
                if (row.hasPostlogs && !row.totals && row.type === 4) {
                  return (
                    <IconButton
                      appearance={Appearance.EMPTY}
                      onClick={() => {
                        setAreYouSure({
                          title: 'You are marking an Upfront Buy as DNR!',
                          message:
                            'THIS IS AN UPFRONT BUY, PLEASE CHECK WITH YOUR BUYER BEFORE MARKING DNR',
                          okayText: 'Save anyway',
                          onOkay: () => {
                            updateReconcileStatus({ ...row, status: 'DNR' });
                          }
                        });
                      }}
                    >
                      <MdError />
                    </IconButton>
                  );
                }
                if (row.hasPostlogs && !row.totals) {
                  return (
                    <IconButton
                      appearance={Appearance.EMPTY}
                      onClick={async () => updateReconcileStatus({ ...row, status: 'DNR' })}
                    >
                      <MdError />
                    </IconButton>
                  );
                }
                return null;
              }
            }
          ]
        : []),
      ...(!expanded
        ? [
            {
              label: 'Clear Post',
              name: 'clear_postlog',
              width: 60,
              renderer: row => (row.clear_postlog ? <MdCheck /> : null)
            }
          ]
        : []),
      ...(expanded
        ? [{ label: 'Len', name: 'length', sortPriority: 4, sortAscending: true, width: 60 }]
        : []),
      ...(expanded
        ? [{ label: 'Days', name: 'dow', sortPriority: 5, sortAscending: true, width: 140 }]
        : []),
      ...(expanded
        ? [{ label: 'Daypart', name: 'daypart', sortPriority: 6, sortAscending: true, width: 120 }]
        : []),
      ...(expanded ? [{ label: 'Rotation', name: 'rotation', sortPriority: 7, width: 300 }] : []),
      ...(expanded
        ? [
            {
              label: 'Type',
              name: 'type',
              width: 70,
              renderer: row => TYPES_TO_NAMES[`${row.type}`]
            }
          ]
        : []),
      ...(expanded
        ? [{ label: 'Media Classification', name: 'mediaClassification', width: 150 }]
        : []),
      ...(expanded ? [{ label: 'Campaign', name: 'campaign', width: 150 }] : []),
      ...(expanded
        ? [
            {
              label: 'Cost',
              name: 'cost',
              width: 60,
              renderer: row => (
                <NumberFormatter {...currencyProps(enableCAD)} value={row.cost} type='$' />
              )
            }
          ]
        : []),
      createDayHeader('booked', 0),
      createDayHeader('booked', 1),
      createDayHeader('booked', 2),
      createDayHeader('booked', 3),
      createDayHeader('booked', 4),
      createDayHeader('booked', 5),
      createDayHeader('booked', 6),
      {
        label: 'Count',
        name: 'booked_count',
        width: 60,
        renderer: row => <NumberFormatter value={row.booked_count} type='#' />
      },
      {
        label: 'Spend',
        name: 'booked_spend',
        renderer: row => (
          <NumberFormatter {...currencyProps(enableCAD)} value={row.booked_spend} type='$' />
        )
      },
      createDayHeader('cleared', 0, true),
      createDayHeader('cleared', 1, true),
      createDayHeader('cleared', 2, true),
      createDayHeader('cleared', 3, true),
      createDayHeader('cleared', 4, true),
      createDayHeader('cleared', 5, true),
      createDayHeader('cleared', 6, true),
      {
        label: 'Count',
        name: 'cleared_count',
        width: 60,
        renderer: row => <NumberFormatter value={row.cleared_count} type='#' />
      },
      {
        label: 'Spend',
        name: 'cleared_spend',
        renderer: row => (
          <NumberFormatter {...currencyProps(enableCAD)} value={row.cleared_spend} type='$' />
        )
      },
      {
        label: 'Clear Rate',
        name: 'clear_rate',
        width: 70,
        renderer: row => <NumberFormatter value={row.estimateInfo?.clearanceRate} type='%' />
      },
      {
        label: 'Hist Clear',
        name: 'hist_clear',
        width: 70,
        renderer: row => (
          <NumberFormatter value={row.estimateInfo?.historicalClearanceRate} type='%' />
        )
      },
      ...(expanded ? [{ label: 'ID', name: 'id' }] : [])
    ],
    [createDayHeader, startWeek, endWeek, expanded, updateReconcileStatus, setAreYouSure, enableCAD]
  );

  const superHeaders = useMemo<SuperTopItemType<string>[]>(() => {
    const bookedStart = planDetailHeaders.findIndex(elem => elem.name?.includes('booked'));
    const clearedStart = planDetailHeaders.findIndex(elem => elem.name?.includes('cleared'));
    const clearedEnd = planDetailHeaders.findLastIndex(elem => elem.name?.includes('clear'));
    return [
      { data: '', span: bookedStart },
      { data: 'Booked', span: clearedStart - bookedStart },
      { data: 'Cleared', span: clearedEnd - clearedStart },
      { data: '', span: planDetailHeaders.length - clearedEnd }
    ];
  }, [planDetailHeaders]);

  const allPlanDetailData = useMemo<PlanRow[]>(() => {
    if (!data) {
      return [];
    }

    return data.plans.map(plan => ({
      ...plan,
      clearance: clearanceForRow({
        dnr: data.dnr,
        estimateInfo: plan.estimateInfo,
        week: plan.week,
        network: plan.network,
        avail: plan.avail
      }),
      hasFullPrelog: plan.estimateInfo.prelogLastDate === 6
    }));
  }, [data]);

  const planDetailData = useMemo<PlanRow[]>(() => {
    if (!data || !data.plans) {
      return [];
    }
    const weekHasPostlogs: Record<string, boolean> = {};
    for (const row of data.spots) {
      weekHasPostlogs[row.week] = weekHasPostlogs[row.week] || (!row.prelog && !row.bvs);
    }

    const grouped = R.groupBy(
      (row: PlanRow) => (expanded ? String(row.id) : `${row.week}_${row.network}_${row.avail}`),
      data.plans
    );

    const rows = Object.values(grouped).map((filteredPlanDetail: PlanRow[]): PlanRow => {
      const first = filteredPlanDetail[0];
      const booked = R.range(0, 7).map(index => ({
        count: R.sum(filteredPlanDetail.map(p => p.booked[index]?.count ?? 0)),
        spend: R.sum(filteredPlanDetail.map(p => p.booked[index]?.spend ?? 0))
      }));
      const cleared = R.range(0, 7).map(index => ({
        count: R.sum(filteredPlanDetail.map(p => p.cleared[index]?.count ?? 0)),
        spend: R.sum(filteredPlanDetail.map(p => p.cleared[index]?.spend ?? 0))
      }));
      let bookedSpendRemaining = R.sum(booked.map(row => row.spend));
      let cleared_spend = 0;
      for (let i = 0; i < 7; i++) {
        if (cleared[i]) {
          if (cleared[i].spend >= bookedSpendRemaining) {
            cleared[i].spend = bookedSpendRemaining;
            bookedSpendRemaining = 0;
          } else {
            bookedSpendRemaining -= cleared[i].spend;
          }
          cleared_spend += cleared[i].spend;
        }
      }

      const estimateInfo: EstimateInfo = {
        clearedSpend: R.sum(filteredPlanDetail.map(p => p.estimateInfo.clearedSpend)),
        orderedSpend: R.sum(filteredPlanDetail.map(p => p.estimateInfo.orderedSpend)),
        estimatedClearedSpend: R.sum(
          filteredPlanDetail.map(p => p.estimateInfo.estimatedClearedSpend)
        ),
        futureOrderedSpend: R.sum(filteredPlanDetail.map(p => p.estimateInfo.futureOrderedSpend)),
        historicalClearanceRate: first.estimateInfo.historicalNetworkAvailClearanceRate,
        prelogLastDate: first.estimateInfo.prelogLastDate,
        goal: first.estimateInfo.goal,
        goal_above: first.estimateInfo.goal_above,
        goal_below: first.estimateInfo.goal_below
      };
      estimateInfo.clearanceRate =
        estimateInfo.orderedSpend + estimateInfo.futureOrderedSpend > 0
          ? (estimateInfo.clearedSpend + estimateInfo.estimatedClearedSpend) /
            (estimateInfo.orderedSpend + estimateInfo.futureOrderedSpend)
          : 0;

      const clearance = clearanceForRow({
        dnr: data.dnr,
        estimateInfo,
        week: first.week,
        network: first.network,
        avail: first.avail
      });

      return {
        ...first,
        bvs: filteredPlanDetail.some(p => Boolean(p.bvs)),
        prelog: filteredPlanDetail.some(p => Boolean(p.prelog)),
        postlog: filteredPlanDetail.some(p => Boolean(p.postlog)),
        hasPostlogs: weekHasPostlogs[first.week],
        dnr: data.dnr.some(
          row =>
            row.week === first.week && row.network === first.network && row.avail === first.avail
        ),
        clear_postlog: filteredPlanDetail.some(p => Boolean(p.clear_postlog)),
        estimateInfo,
        ...Object.fromEntries(
          PLAN_DETAIL_KEYS_TO_SUM.map(key => [
            key,
            R.sum(filteredPlanDetail.map(p => Number(p[key] ?? 0)))
          ])
        ),
        cleared_spend,
        booked,
        cleared,
        clearance,
        hasFullPrelog: estimateInfo.prelogLastDate === 6
      };
    });

    return rows.filter(planDetailFilter);
  }, [data, expanded, planDetailFilter]);

  const planDetailTotalsRow = useMemo<Record<string, unknown>>(() => {
    const booked = R.range(0, 7).map(index => ({
      booked_count: R.sum(planDetailData.map(p => p.booked[index]?.count ?? 0)),
      booked_spend: R.sum(planDetailData.map(p => p.booked[index]?.spend ?? 0))
    }));
    const cleared = R.range(0, 7).map(index => ({
      cleared_count: R.sum(planDetailData.map(p => p.cleared[index]?.count ?? 0)),
      cleared_spend: R.sum(planDetailData.map(p => p.cleared[index]?.spend ?? 0))
    }));
    let bookedSpendRemaining = R.sum(booked.map(row => row.booked_spend));

    let cleared_spend = 0;
    for (let i = 0; i < 7; i++) {
      if (cleared[i]) {
        if (cleared[i].cleared_spend >= bookedSpendRemaining) {
          cleared[i].cleared_spend = bookedSpendRemaining;
          bookedSpendRemaining = 0;
        } else {
          bookedSpendRemaining -= cleared[i].cleared_spend;
        }
        cleared_spend += cleared[i].cleared_spend;
      }
    }

    return {
      ...Object.fromEntries(
        PLAN_DETAIL_KEYS_TO_SUM.map(key => [
          key,
          R.sum(planDetailData.map(p => Number(p[key] ?? 0)))
        ])
      ),
      cleared_spend,
      booked,
      cleared
    };
  }, [planDetailData]);

  const planDetailTotalsRenderer = useCallback<CellRenderer<Element | number | string | undefined>>(
    ({ columnIndex, style = {}, classes = [] }) => {
      const header = planDetailHeaders[columnIndex];
      const rendered = header.renderer
        ? header.renderer({ ...planDetailTotalsRow, totals: true })
        : planDetailTotalsRow[header.name ?? ''];
      return (
        <div style={style} className={cn(...classes, 'grand-total-cell')}>
          {/* rendered is a dynamic table cell value (number/string/JSX) keyed by column */}
          {rendered as ReactNode}
        </div>
      );
    },
    [planDetailTotalsRow, planDetailHeaders]
  );

  const allSpotDetailData = useMemo<SpotRow[]>(() => {
    if (!data) {
      return [];
    }
    const sortKey = (row: SpotRow) =>
      [
        row.week,
        row.network,
        row.avail,
        row.isci,
        row.datetime_broadcast_text,
        row.datetime_calendar_text
      ]
        .map(value => value ?? '')
        .join('_');
    return [...data.spots].sort((a, b) => {
      const keyA = sortKey(a);
      const keyB = sortKey(b);
      if (keyA < keyB) return -1;
      if (keyA > keyB) return 1;
      return 0;
    });
  }, [data]);

  const spotDetailData = useMemo(
    () => allSpotDetailData.filter(spotDetailFilter),
    [allSpotDetailData, spotDetailFilter]
  );

  const spotDetailTotalsRow = useMemo(
    () => ({
      spend: R.sum(spotDetailData.map(row => row.cost)),
      credit_amount: R.sum(spotDetailData.map(row => row.credit_amount)),
      count: spotDetailData.length
    }),
    [spotDetailData]
  );

  const spotDetailTotalsRenderer = useCallback<CellRenderer<Element | number | string | undefined>>(
    ({ columnIndex, style = {}, classes = [] }) => {
      const { label } = spotDetailHeaders[columnIndex];
      let content: ReactNode = null;
      if (label === 'ISCI') {
        content = <NumberFormatter value={spotDetailTotalsRow.count} type='#' />;
      } else if (label === 'Cost') {
        content = (
          <NumberFormatter
            {...currencyProps(enableCAD)}
            value={spotDetailTotalsRow.spend}
            type='$'
          />
        );
      } else if (label === 'Credit Amount') {
        content = (
          <NumberFormatter
            {...currencyProps(enableCAD)}
            value={spotDetailTotalsRow.credit_amount}
            type='$'
          />
        );
      }

      return (
        <div style={style} className={cn(...classes, 'grand-total-cell')}>
          {content}
        </div>
      );
    },
    [spotDetailTotalsRow, spotDetailHeaders, enableCAD]
  );

  const updateFiltered = useCallback(
    (newVal: boolean) => {
      if (!data) {
        return;
      }
      const filteredIds = spotDetailData.map(row => row.id);
      setData({
        ...data,
        spots: data.spots.map(spotRow =>
          filteredIds.includes(spotRow.id) ? { ...spotRow, selected: newVal } : spotRow
        )
      });
    },
    [data, spotDetailData]
  );

  // Drives the select-all header checkbox. Derived (rather than a separate boolean) so it
  // stays correct when the filter changes or rows are edited/deleted out of the selection.
  const allVisibleSelected = useMemo(
    () => spotDetailData.length > 0 && spotDetailData.every(row => row.selected),
    [spotDetailData]
  );

  // Renders a select-all checkbox in the (internal-only) first header cell; it selects or
  // clears every currently-visible (filtered) row, matching the old Select/Clear Visible buttons.
  const spotHeadersRenderer = useCallback<CellRenderer<Header>>(
    ({ data: headerLabel, columnIndex }) => {
      if (isInternal && columnIndex === 0) {
        return (
          <OverlayTrigger
            placement={Placement.BOTTOM}
            overlay={<Tooltip>Select or clear all visible rows</Tooltip>}
          >
            <span>
              <Checkbox
                checked={allVisibleSelected}
                onChange={() => updateFiltered(!allVisibleSelected)}
              />
            </span>
          </OverlayTrigger>
        );
      }
      return <>{headerLabel}</>;
    },
    [isInternal, allVisibleSelected, updateFiltered]
  );

  const spotDetailCsv = useMemo(() => {
    const header = [...spotDetailHeaders.map(h => h.label ?? ''), 'Calendar Time (ET)'];
    const rows = spotDetailData.map(row => [
      ...spotDetailHeaders.map(h => (h.name ? row[h.name] : '')),
      row.datetime_calendar_text
    ]);

    rows.unshift(header);
    return Papa.unparse(rows);
  }, [spotDetailData, spotDetailHeaders]);

  const spotsDirty = useMemo(
    () => Boolean(data?.spots && data.spots.some(row => row.dirty)),
    [data]
  );

  const selectedSpots = useMemo(() => data?.spots?.filter(row => row.selected) ?? [], [data]);

  const validDeletionRows = useMemo(() => {
    if (!data || !data.spots) {
      return false;
    }
    const countByNetwork: Record<string, number> = {};
    const countSelectedByNetwork: Record<string, number> = {};
    for (const row of data.spots) {
      countByNetwork[row.network] = (countByNetwork[row.network] ?? 0) + 1;
      if (row.selected) {
        countSelectedByNetwork[row.network] = (countSelectedByNetwork[row.network] ?? 0) + 1;
      }
    }

    // Only allow deletion when every network with a selected spot has all of its spots selected.
    return Object.keys(countSelectedByNetwork).every(
      network => countSelectedByNetwork[network] === countByNetwork[network]
    );
  }, [data]);

  const planDetailOptions = useMemo<FilterColumn[]>(
    () => [
      { label: 'Clearance', name: 'clearance' },
      { label: 'Full Prelog', name: 'hasFullPrelog' },
      ...planDetailHeaders
        .filter(header =>
          [
            'network',
            'avail',
            'length',
            'dow',
            'daypart',
            'rotation',
            'type',
            'week',
            'clearance',
            'hasFullPrelog',
            'mediaClassification',
            'campaign'
          ].includes(header.name ?? '')
        )
        .map(header => ({ label: header.label ?? '', name: header.name }))
    ],
    [planDetailHeaders]
  );

  const renderTableLoading = useCallback(() => <TableLoading fetching={isLoading} />, [isLoading]);

  const superHeadersRenderer = useCallback<CellRenderer<string>>(
    ({ data: superData, style, classes }) => {
      // If it's an empty super header, don't add the underline
      if (superData) {
        classes.push('super-header');
      }
      return (
        <div style={style} className={cn(classes)}>
          {superData}
        </div>
      );
    },
    []
  );

  const spotDetailOptions = useMemo<FilterColumn[]>(
    () =>
      spotDetailHeaders
        .filter(
          header =>
            [
              'network',
              'avail',
              'length',
              'type',
              'isci',
              'week',
              'rotation',
              'datetime_broadcast_text',
              'datetime_calendar_text',
              'program',
              'notes',
              'mediaClassification',
              'campaign'
            ].includes(header.name ?? '') ||
            (spotFilterAdvanced && ['accepted', 'creditMemoStatus'].includes(header.name ?? ''))
        )
        .map(header => ({ label: header.label ?? '', name: header.name })),
    [spotDetailHeaders, spotFilterAdvanced]
  );

  return (
    <Page
      title='Linear Plans'
      pageType='Linear Plans'
      minHeight='600px'
      actions={actions}
      isLoading={isLoading}
    >
      <div className='linear-plans'>
        <div className='all-panes'>
          <div className='top-pane'>
            <div className='filter-bar-container'>
              <FilterBar
                size={Size.LG}
                hasAdvanced
                options={planDetailOptions}
                lines={allPlanDetailData}
                onFilter={setPlanDetailFilter}
                advanced={planFilterAdvanced}
                onSetAdvanced={setPlanFilterAdvanced}
              />
              <IconButton
                tooltip={{ text: 'Download plans CSV' }}
                onClick={() =>
                  download(
                    planDetailCsv,
                    `Plans ${company} ${startWeek} - ${endWeek}.csv`,
                    'text/csv'
                  )
                }
              >
                <MdOutlineFileDownload />
              </IconButton>
            </div>
            <BPMTable
              headers={planDetailHeaders}
              data={planDetailData}
              rowHeight={26}
              headerHeight={40}
              filterBar={false}
              noRowsRenderer={renderTableLoading}
              superHeaders={superHeaders}
              superHeadersRenderer={superHeadersRenderer}
              totals={planDetailTotalsRow}
              totalsRenderer={planDetailTotalsRenderer}
            />
          </div>
          <div className='bottom-pane'>
            <div className='filter-bar-container'>
              <FilterBar
                size={Size.LG}
                hasAdvanced
                options={spotDetailOptions}
                lines={allSpotDetailData}
                onFilter={setSpotDetailFilter}
                advanced={spotFilterAdvanced}
                onSetAdvanced={setSpotFilterAdvanced}
              />
              <IconButton
                tooltip={{ text: 'Download spots CSV' }}
                onClick={() =>
                  download(
                    spotDetailCsv,
                    `Spots ${company} ${startWeek} - ${endWeek}.csv`,
                    'text/csv'
                  )
                }
              >
                <MdOutlineFileDownload />
              </IconButton>
              {isInternal && (
                <IconButton
                  tooltip={{ text: 'Save changes' }}
                  loading={saving}
                  disabled={!spotsDirty}
                  onClick={updateSpots}
                >
                  <MdSave />
                </IconButton>
              )}
              {isInternal && (
                <IconButton
                  tooltip={{ text: 'Edit selected rows' }}
                  disabled={selectedSpots.length === 0 || !isEditable}
                  onClick={() => setShowEditSelectedRows(true)}
                >
                  <MdEdit />
                </IconButton>
              )}
              {isInternal && (
                <IconButton
                  tooltip={{ text: 'Delete selected rows' }}
                  design={Intent.ERROR}
                  loading={saving}
                  disabled={!validDeletionRows || selectedSpots.length === 0}
                  onClick={() =>
                    setAreYouSure({
                      title: 'Delete Selected Rows?',
                      message: `Delete ${conditionallyPluralizeWithCount(
                        selectedSpots.length,
                        'selected row'
                      )}? This cannot be undone.`,
                      okayText: 'Delete Rows',
                      intent: Intent.ERROR,
                      onOkay: handleDeleteSpots
                    })
                  }
                >
                  <MdDeleteForever />
                </IconButton>
              )}
              <Button
                size={Size.LG}
                appearance={Appearance.OUTLINED}
                onClick={() => setShowExportSpotSummaryConfirmation(true)}
              >
                Summary
              </Button>
            </div>
            <BPMTable
              headers={spotDetailHeaders}
              data={spotDetailData}
              rowHeight={26}
              filterBar={false}
              headersRenderer={spotHeadersRenderer}
              onUpdateSortParamList={setSpotDetailSortParamList}
              overrideSortParamList={spotDetailSortParamList}
              noRowsRenderer={renderTableLoading}
              totals={spotDetailTotalsRow}
              totalsRenderer={spotDetailTotalsRenderer}
            />
          </div>
        </div>
        {showExportSpotSummaryConfirmation && (
          <ExportSpotSummaryConfirmation
            company={company}
            show={showExportSpotSummaryConfirmation}
            setShow={setShowExportSpotSummaryConfirmation}
          />
        )}
        {showEditSelectedRows && (
          <EditSpotsModal
            selectedSpots={selectedSpots}
            updateSelectedSpots={newValues => {
              if (!data) {
                return;
              }
              const selectedIds = selectedSpots.map(spot => spot.id);
              setData({
                ...data,
                spots: data.spots.map(spotRow =>
                  selectedIds.includes(spotRow.id)
                    ? { ...spotRow, ...newValues, dirty: true, selected: false }
                    : spotRow
                )
              });
            }}
            show={showEditSelectedRows}
            setShow={setShowEditSelectedRows}
          />
        )}
        <CreditMemoToasts
          nCreditMemosToast={nCreditMemosToast}
          setNCreditMemosToast={setNCreditMemosToast}
          showMakingCreditMemoToast={showMakingCreditMemoToast}
          setShowMakingCreditMemoToast={setShowMakingCreditMemoToast}
        />
      </div>
    </Page>
  );
};
